import { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, GeoJSON, CircleMarker, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api } from "@/lib/api";

const ONTARIO_BOUNDS = [[41.6, -95.2], [56.9, -74.3]];
const SOUTH_ONTARIO_BOUNDS = [[42.0, -84.0], [46.5, -74.3]];

let _muniPromise = null;
export const loadMunicipalities = () => {
  if (!_muniPromise) {
    _muniPromise = api.get("/public/geo/municipalities").then((r) => r.data);
  }
  return _muniPromise;
};

const wardCache = {};
export const loadWards = async (slug) => {
  if (!(slug in wardCache)) {
    try {
      const r = await api.get(`/public/geo/wards/${slug}`);
      wardCache[slug] = r.data;
    } catch {
      wardCache[slug] = null;
    }
  }
  return wardCache[slug];
};

const FitController = ({ focusBounds }) => {
  const map = useMap();
  useEffect(() => {
    if (focusBounds) {
      map.fitBounds(focusBounds, { padding: [24, 24] });
    }
  }, [focusBounds, map]);
  return null;
};

/**
 * Shared Ontario map.
 * - Municipality outlines always shown (simplified LIO Open Data boundaries).
 * - Ward outlines load for the focused/selected municipality where data exists.
 * - Optional point marker (geocoded address).
 */
export const OntarioMapView = ({
  focusMuniSlug = null,
  focusWardLabel = null,
  point = null,
  onSelectMunicipality = null,
  onSelectWard = null,
  heightClass = "h-[420px] sm:h-[520px]",
  southOntario = true,
}) => {
  const [munis, setMunis] = useState(null);
  const [wards, setWards] = useState(null);
  const [focusBounds, setFocusBounds] = useState(null);
  const selectedRef = useRef(focusMuniSlug);
  selectedRef.current = focusMuniSlug;

  useEffect(() => {
    loadMunicipalities().then(setMunis).catch(() => setMunis({ type: "FeatureCollection", features: [] }));
  }, []);

  useEffect(() => {
    setWards(null);
    if (!focusMuniSlug) {
      setFocusBounds(null);
      return;
    }
    loadWards(focusMuniSlug).then(setWards);
  }, [focusMuniSlug]);

  useEffect(() => {
    if (!munis || !focusMuniSlug) return;
    const f = munis.features.find((x) => x.properties.slug === focusMuniSlug);
    if (f?.properties?.bbox) {
      const [minx, miny, maxx, maxy] = f.properties.bbox;
      setFocusBounds([[miny, minx], [maxy, maxx]]);
    }
  }, [munis, focusMuniSlug]);

  const muniStyle = useMemo(
    () => (feature) => {
      const selected = feature.properties.slug === selectedRef.current;
      return {
        color: selected ? "#0f766e" : "#1e3a5f",
        weight: selected ? 2.5 : 1,
        fillColor: selected ? "#14b8a6" : "#3b82f6",
        fillOpacity: selected ? 0.14 : 0.05,
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [focusMuniSlug]
  );

  const onEachMuni = (feature, layer) => {
    layer.bindTooltip(feature.properties.name, { sticky: true, direction: "top" });
    layer.on({
      click: () => onSelectMunicipality && onSelectMunicipality(feature.properties),
      mouseover: (e) => e.target.setStyle({ fillOpacity: 0.22 }),
      mouseout: (e) => e.target.setStyle(muniStyle(feature)),
    });
  };

  const wardStyle = (feature) => {
    const selected = focusWardLabel && feature.properties.ward_label === focusWardLabel;
    return {
      color: selected ? "#b45309" : "#0f766e",
      weight: selected ? 2.5 : 1.5,
      fillColor: selected ? "#f59e0b" : "#14b8a6",
      fillOpacity: selected ? 0.22 : 0.08,
      dashArray: "4 3",
    };
  };

  const onEachWard = (feature, layer) => {
    layer.bindTooltip(feature.properties.ward_label, { sticky: true, direction: "top" });
    layer.on({ click: () => onSelectWard && onSelectWard(feature.properties) });
  };

  return (
    <div className={`relative z-0 rounded-xl overflow-hidden border border-slate-200 ${heightClass}`} data-testid="ontario-map">
      <MapContainer
        bounds={southOntario ? SOUTH_ONTARIO_BOUNDS : ONTARIO_BOUNDS}
        scrollWheelZoom={true}
        className="h-full w-full"
        attributionControl={true}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {munis && (
          <GeoJSON key={`munis-${focusMuniSlug || "all"}`} data={munis} style={muniStyle} onEachFeature={onEachMuni} />
        )}
        {wards && (
          <GeoJSON key={`wards-${focusMuniSlug}-${focusWardLabel || "none"}`} data={wards} style={wardStyle} onEachFeature={onEachWard} />
        )}
        {point && (
          <CircleMarker
            center={[point.lat, point.lon]}
            radius={8}
            pathOptions={{ color: "#b91c1c", fillColor: "#ef4444", fillOpacity: 0.9, weight: 2 }}
          >
            <Tooltip permanent direction="top" offset={[0, -8]}>Your address</Tooltip>
          </CircleMarker>
        )}
        <FitController focusBounds={focusBounds || (point ? [[point.lat - 0.05, point.lon - 0.08], [point.lat + 0.05, point.lon + 0.08]] : null)} />
      </MapContainer>
    </div>
  );
};
